/**
 * 市民端接口调用点。路径全部照 contract.ts（＝服务端 citizen-dto.ts）的注释抄。
 *
 * 真本注释只钉死了补充材料那条 `POST /api/citizen/my/cases/:billNo/supplement`；
 * 列表与详情的路径按同一前缀取，并已对真后端（3001）**实测确认**：
 * 路由表里就是 `GET /api/citizen/my/cases` 与 `GET /api/citizen/my/cases/:billNo`。
 */

import { apiGet, apiPost, apiPostWrite, qs, API_BASE, CREDIT_TIMEOUT_MS } from './client';
import { WRITE_CHECK_WHERE } from './write-timeout';
import type {
  CitizenCaseBrief,
  CitizenCaseDetail,
  CitizenConsultResult,
  CitizenSendCodeResult,
  CitizenSubmitBody,
  CitizenSubmitResult,
  CitizenVerifyResult,
  CreditBlacklistResult,
  CreditSearchResult,
  CreditSincerityResult,
  CreditSummary,
  CreditUserDetail,
} from './contract';

const P = '/api/citizen';

/* ---------------- 6.1 提交（不验证码） ---------------- */

/**
 * 提交投诉。
 *
 * ★ 超时时长按 `body.证据` 的体积算，**不吃全局那 8 秒**；超时话术也不说"失败"。
 *   两半都在 `apiPostWrite` 里一起给（理由见 `client.ts` 的 `writeRequestInit`
 *   与 `write-timeout.ts` 文件头那次「提了三遍」的事故）。
 */
export function submitComplaint(body: CitizenSubmitBody): Promise<CitizenSubmitResult> {
  return apiPostWrite<CitizenSubmitResult>(
    `${P}/complaints`,
    body,
    body.证据,
    WRITE_CHECK_WHERE.complaint,
  );
}

/* ---------------- 6.1 手机号 ＋ 验证码 ---------------- */

export function sendCode(phone: string): Promise<CitizenSendCodeResult> {
  return apiPost<CitizenSendCodeResult>(`${P}/auth/send-code`, { phone });
}

export function verifyCode(phone: string, code: string): Promise<CitizenVerifyResult> {
  return apiPost<CitizenVerifyResult>(`${P}/auth/verify`, { phone, code });
}

/* ---------------- 6.1 我的投诉 ---------------- */

/**
 * 列表。**实测**（2026-08-25 对 3001 真后端）返回的是
 * `{ phoneMasked, total, items: CitizenCaseBrief[] }`，不是裸数组——
 * 契约真本没写这个外层，所以这里两种都吃下，并把实测形状记在案，
 * 待 cc 把外层补进 `citizen-dto.ts` 后再收敛成一支。
 */
export async function listMyCases(): Promise<CitizenCaseBrief[]> {
  const data = await apiGet<unknown>(`${P}/my/cases`);
  if (Array.isArray(data)) return data as CitizenCaseBrief[];
  if (typeof data === 'object' && data !== null) {
    const items = (data as { items?: unknown }).items;
    if (Array.isArray(items)) return items as CitizenCaseBrief[];
  }
  throw new Error('「我的投诉」返回形状与契约不符（既不是数组也没有 items）');
}

export function getCaseDetail(billNo: string): Promise<CitizenCaseDetail> {
  return apiGet<CitizenCaseDetail>(`${P}/my/cases/${encodeURIComponent(billNo)}`);
}

/*
 * ★ v1 的 `postSupplement()`（POST .../supplement，「追加一段说明」模型）**前端已不再调用**。
 *   规格 §四 要的是「在原有投诉表单上修改」，见 `./amend.ts` 的 `postAmend()`。
 *   后端那条路由仍在（老客户端与冒烟还在用），但内部已经并进同一条补录管线，
 *   两边看到的是同一份轮次记录，不会分叉成两套历史。
 */

/* ---------------- 6.2 诚信公示（全部走服务端代理） ---------------- */

export function creditSummary(): Promise<CreditSummary> {
  return apiGet<CreditSummary>(`${P}/credit/summary`, CREDIT_TIMEOUT_MS);
}

export function creditBlacklist(keyword: string, pageSize = 50): Promise<CreditBlacklistResult> {
  return apiGet<CreditBlacklistResult>(
    `${P}/credit/blacklist${qs({ keyword, pageIndex: 1, pageSize })}`,
    CREDIT_TIMEOUT_MS,
  );
}

/**
 * 诚信名单。
 *
 * ★ **只取失信档（state=1）**（2026-08-26 主公定）。预警档（=2）实测 251,734 条，
 *   那是在册人员的默认档位，不是「被预警的人」；把它当名单公示，
 *   等于把绝大多数正常从业者挂上去。所以这个函数**只接受 1**。
 * ★ 失信档服务端走整桶模式（273 条翻完再按记录自身状态过滤成 248 条），
 *   一次就把完整名单给全，前端不再分页。
 */
export function creditSincerity(state: 1 = 1): Promise<CreditSincerityResult> {
  return apiGet<CreditSincerityResult>(
    `${P}/credit/sincerity${qs({ state, pageIndex: 1, pageSize: 100 })}`,
    CREDIT_TIMEOUT_MS,
  );
}

/** 经纪人详情。`userId` 取自诚信记录条目自带的 `userId`（为 0 时不要调）。 */
export function creditUserDetail(userId: number): Promise<CreditUserDetail> {
  return apiGet<CreditUserDetail>(
    `${P}/credit/user/${encodeURIComponent(String(userId))}`,
    CREDIT_TIMEOUT_MS,
  );
}

/** 检索经纪人：**姓名或 CD 编号都可以**（2026-09-06 起编号也通了）。 */
export function creditSearch(name: string, pageSize = 50): Promise<CreditSearchResult> {
  // 五档串行查，实测 7 秒起步 —— 默认 8 秒超时会把它自己判失败
  return apiGet<CreditSearchResult>(`${P}/credit/search${qs({ name, pageSize })}`, CREDIT_TIMEOUT_MS);
}

/* ---------------- 6.3 咨询 ---------------- */

export function consult(question: string): Promise<CitizenConsultResult> {
  return apiPost<CitizenConsultResult>(`${P}/consult`, { question });
}

/* ---------------- 语音输入 · 一句话识别（公开，不带令牌） ---------------- */

/**
 * 把一段录音转成文字。
 *
 * ★ `wavBase64` 必须是 **16kHz 单声道 16-bit PCM 的 WAV** 文件的 base64（不带 `data:` 前缀）——
 *   编码在 `hooks/useVoiceRecorder.ts` 里做，火山 v2 只认 wav/pcm，格式不对识别不了。
 * ★ 和 `/consult` 一样是公开能力，不带令牌；免验证码。
 * ★ 成功经 `apiPost` 拆信封后拿到 `{ text }`；失败 `apiPost` 抛 `ApiError`，`err.message` 是中文原因。
 */
export function recognizeVoice(wavBase64: string): Promise<{ text: string }> {
  return apiPost<{ text: string }>(`${P}/asr/recognize`, {
    audio: wavBase64,
    format: 'wav',
    rate: 16000,
  });
}

/* ---------------- 6.3 咨询（流式，多轮） ---------------- */

/**
 * 出处条目：**只显示 title**。上游知识库页面链接对公众有登录墙，`url` 收着不做跳转。
 */
export interface ConsultSourceLink {
  title: string;
  url?: string;
}

/**
 * 流式事件回调。后端把上游平台事件**归一化**成 `data:{type,...}` 逐行下发，
 * 前端只认 `type`，内层字段名做容错（不同实现可能叫 text/delta/content）。
 */
export interface ConsultStreamHandlers {
  /** 检索进度提示，如「正在检索协会知识库…」 */
  onStatus?: (message: string) => void;
  /** 正文增量，拼接后逐字显示 */
  onDelta?: (text: string) => void;
  /** 出处列表（可折叠，仅显示 title） */
  onSources?: (sources: ConsultSourceLink[]) => void;
  /** 收尾：带回 sessionId 供多轮；answered=false 表示后端如实答不上来 */
  onDone?: (info: { sessionId?: string; answered?: boolean }) => void;
  /** 后端明确「答不上来」 */
  onNoAnswer?: (message?: string) => void;
  /** 流出错 */
  onError?: (message: string) => void;
}

function normalizeSources(raw: unknown): ConsultSourceLink[] {
  if (!Array.isArray(raw)) return [];
  const out: ConsultSourceLink[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const title = typeof o['title'] === 'string' ? o['title'].trim() : '';
    if (!title || seen.has(title)) continue;
    seen.add(title);
    const url = typeof o['url'] === 'string' ? o['url'] : undefined;
    out.push(url ? { title, url } : { title });
  }
  return out;
}

/** 把一条归一化事件派发给对应回调。内层字段名做容错。 */
function dispatchConsultEvent(o: Record<string, unknown>, h: ConsultStreamHandlers): void {
  switch (o['type']) {
    case 'status':
      h.onStatus?.(String(o['message'] ?? o['text'] ?? ''));
      break;
    case 'delta': {
      const t = o['text'] ?? o['delta'] ?? o['content'] ?? '';
      if (typeof t === 'string' && t) h.onDelta?.(t);
      break;
    }
    case 'sources':
      h.onSources?.(normalizeSources(o['sources'] ?? o['docs'] ?? o['reference_docs']));
      break;
    case 'done':
      h.onDone?.({
        sessionId:
          typeof o['sessionId'] === 'string'
            ? o['sessionId']
            : typeof o['session_id'] === 'string'
              ? (o['session_id'] as string)
              : undefined,
        answered: typeof o['answered'] === 'boolean' ? o['answered'] : undefined,
      });
      break;
    case 'noanswer':
      h.onNoAnswer?.(typeof o['message'] === 'string' ? o['message'] : undefined);
      break;
    case 'error':
      h.onError?.(String(o['message'] ?? o['error'] ?? '服务出错'));
      break;
    default:
      break;
  }
}

/**
 * 多轮流式咨询。
 *
 * `request()` 走 `res.text()` 一次性拿完，不能流式；这里直接用 `fetch` ＋ `ReadableStream`
 * reader 边收边解析。逐行找 `data:` 前缀，解析归一化事件。
 *
 * ★ 咨询是**公开**能力，不带令牌（与非流式 `/consult` 一致）。
 * ★ 网络/HTTP 错误一律走 `onError`（`AbortError` 视为主动取消，静默返回），
 *   由调用方如实显示「答不上来 ＋ 致电协会热线」，**绝不编兜底答案**。
 */
export async function consultStream(
  question: string,
  sessionId: string,
  handlers: ConsultStreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${P}/consult/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question, sessionId }),
      signal,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return;
    handlers.onError?.('连不上服务端');
    return;
  }

  if (!res.ok || !res.body) {
    handlers.onError?.(`服务未就绪（HTTP ${res.status}）`);
    return;
  }

  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      let idx: number;
      while ((idx = buf.indexOf('\n')) >= 0) {
        const line = buf.slice(0, idx);
        buf = buf.slice(idx + 1);
        const t = line.replace(/\r$/, '').trim();
        // 空行、SSE 注释、event: 行都跳过，只认 data:
        if (!t || t.startsWith(':') || t.startsWith('event:')) continue;
        if (!t.startsWith('data:')) continue;
        const js = t.slice(5).trim();
        if (!js || js === '[DONE]') continue;
        let o: unknown;
        try {
          o = JSON.parse(js);
        } catch {
          continue;
        }
        if (o && typeof o === 'object') dispatchConsultEvent(o as Record<string, unknown>, handlers);
      }
    }
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return;
    handlers.onError?.('连接中断');
  }
}
